import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';

import { ManagerProgressionTaskSlug } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DailyClaimModule } from '../DailyClaimModule';
import type { CommonPacksHeader_marketRoot } from './__generated__/index.graphql';

const Root = styled(Container)`
  background:
    linear-gradient(to bottom, transparent 77%, var(--c-black) 100%),
    var(--c-background-image) bottom center/100% 100% no-repeat;
  gap: var(--double-unit);
  padding: var(--triple-unit) 0;
  justify-content: stretch;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;
const ContentWrapper = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: space-around;
  &.isSpecialEvent {
    justify-content: space-between;
  }
  width: 100%;
  gap: var(--quadruple-unit);
`;
const DailyClaimModuleWrapper = styled.div`
  width: 100%;
  @media ${laptopAndAbove} {
    width: max-content;
  }
`;

type Props = {
  marketRoot: CommonPacksHeader_marketRoot;
  refetch: () => Promise<unknown>;
  backgroundUrl: string;
  title: ReactNode;
  balances: ReactNode;
  isSpecialEvent: boolean;
  children: ReactNode;
};
export const CommonPacksHeader = ({
  marketRoot,
  refetch,
  backgroundUrl,
  title,
  balances,
  isSpecialEvent,
  children,
}: Props) => {
  const { tasks } = marketRoot;

  const dailyClaimTask = tasks
    .filter(isTypeFn('Task'))
    .find(task => task.name === ManagerProgressionTaskSlug.DAILY_CLAIM);

  return (
    <Root
      style={
        { '--c-background-image': `url(${backgroundUrl})` } as CSSProperties
      }
      maxWidth={1600}
    >
      {balances}
      {title}
      <ContentWrapper className={classNames({ isSpecialEvent })}>
        {children}
        {!!dailyClaimTask && (
          <DailyClaimModuleWrapper>
            <DailyClaimModule task={dailyClaimTask} refetch={refetch} />
          </DailyClaimModuleWrapper>
        )}
      </ContentWrapper>
    </Root>
  );
};

CommonPacksHeader.fragments = {
  marketRoot: gql`
    fragment CommonPacksHeader_marketRoot on MarketRoot {
      id
      tasks(sport: $sport) {
        id
        name
        ...DailyClaimModule_task
      }
    }
    ${DailyClaimModule.fragments.task}
  ` as TypedDocumentNode<CommonPacksHeader_marketRoot>,
};
